// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { useEffect, useState } from "react";
import { App as AntApp } from "antd";
import { Login } from "./components/Login";
import { Landing } from "./components/Landing";
import { Workspace } from "./components/Workspace";
import { Terms, Privacy } from "./components/Legal";
import type { Session } from "./types";
import { Homeserver } from "./api";
import { initPush } from "./push";
import { registerNtfyPush, isPushEnabled } from "./push-ntfy";
import { isNativeApp } from "./server";

const STORAGE_KEY = "zalmoxis.session";

// parseOIDCHash reads the result of an OIDC sign-in, which the homeserver hands back
// in the URL fragment (#oidc=<base64url session> | #oidc_error=<message>) — a fragment
// is never sent to the server or written to logs.
function parseOIDCHash(): { session: Session | null; error: string | null } {
  try {
    const h = new URLSearchParams(globalThis.location.hash.replace(/^#/, ""));
    const enc = h.get("oidc");
    if (enc) {
      const b64 = enc.replace(/-/g, "+").replace(/_/g, "/");
      const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
      return { session: JSON.parse(atob(b64 + pad)) as Session, error: null };
    }
    return { session: null, error: h.get("oidc_error") };
  } catch {
    return { session: null, error: null };
  }
}

// hasInviteLink reports whether the visitor arrived via an invite link (?reg=…),
// so we skip the marketing landing page and take them straight to sign-up.
function hasInviteLink(): boolean {
  try {
    return !!new URLSearchParams(globalThis.location.search).get("reg");
  } catch {
    return false;
  }
}

function loadSession(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

function MainApp() {
  const { message } = AntApp.useApp();
  const [session, setSession] = useState<Session | null>(() => parseOIDCHash().session ?? loadSession());
  // Logged-out WEB visitors land on the public home page first; native app users (who already
  // installed it), visitors who tap a CTA, and anyone following an invite link (?reg=…) go
  // straight to the login/register form — an invite opens Register directly.
  const [auth, setAuth] = useState<null | "Log in" | "Register">(() => {
    if (parseOIDCHash().error) return "Log in";
    if (hasInviteLink()) return "Register";
    return isNativeApp() ? "Log in" : null;
  });

  // Consume any OIDC sign-in result from the URL fragment, then strip it.
  useEffect(() => {
    const { error } = parseOIDCHash();
    if (error) message.error(`Sign-in failed: ${error}`);
    else if (globalThis.location.hash.includes("oidc_linked")) message.success("Sign-in provider linked — you can now use it to log in");
    if (globalThis.location.hash.includes("oidc")) {
      globalThis.history.replaceState({}, "", globalThis.location.pathname + globalThis.location.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (session) localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(STORAGE_KEY);
  }, [session]);

  // Native app: register background push — ntfy (UnifiedPush, default) + FCM (gated
  // off until a Firebase project is configured). Both report to the homeserver.
  useEffect(() => {
    if (!session) return;
    if (isPushEnabled()) void registerNtfyPush(session.access_token, session.user_id); // honour the Notifications toggle
    void initPush({ onToken: (pushToken) => void new Homeserver().registerPusher(session.access_token, "fcm", pushToken) });
  }, [session]);

  if (!session) {
    if (auth === null) return <Landing onEnter={setAuth} />;
    return <Login onSession={setSession} initialMode={auth} onBack={isNativeApp() ? undefined : () => setAuth(null)} />;
  }
  // After logout go to the login form (not the marketing landing — they already know the app).
  return (
    <Workspace
      session={session}
      onLogout={() => {
        setSession(null);
        setAuth("Log in");
      }}
    />
  );
}

// currentPath returns the SPA pathname. The homeserver serves index.html for any path
// (web/ui_embed.go catch-all), so the client routes static pages by pathname.
function currentPath(): string {
  try {
    return globalThis.location.pathname;
  } catch {
    return "/";
  }
}

// Static, unauthenticated legal pages are selected by pathname before the auth state
// machine — they render no app hooks, so the early return is safe.
export default function App() {
  const path = currentPath();
  if (path === "/terms") return <Terms />;
  if (path === "/privacy") return <Privacy />;
  return <MainApp />;
}
